/** Sistema de design do Portal Erimus: cabeçalhos, painéis, tabelas, filtros, campos, status e ações. */
import { Search } from "lucide-react";

export const PageHeader = ({ kicker, title, subtitle, actions, testId, titleTestId, subtitleTestId }) => (
  <header data-testid={testId} className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
    <div className="max-w-2xl">
      {kicker ? <p className="er-label text-[12px] tracking-[0.16em]">{kicker}</p> : null}
      <h1
        data-testid={titleTestId}
        className="mt-2 font-display text-[28px] font-semibold leading-[1.2] tracking-tight text-ink-900"
      >
        {title}
      </h1>
      {subtitle ? (
        <p data-testid={subtitleTestId} className="mt-2.5 text-[15px] leading-relaxed text-ink-600">
          {subtitle}
        </p>
      ) : null}
    </div>
    {actions ? <div className="flex flex-wrap items-center gap-2.5">{actions}</div> : null}
  </header>
);

export const Panel = ({ children, className = "", testId, padded = true, ...rest }) => (
  <section
    data-testid={testId}
    className={`rounded-2xl border border-border bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] ${
      padded ? "p-6" : ""
    } ${className}`}
    {...rest}
  >
    {children}
  </section>
);

export const PanelHeader = ({ label, title, hint, actions }) => (
  <div className="flex flex-wrap items-start justify-between gap-3">
    <div>
      {label ? <p className="er-label text-[12px] tracking-[0.16em]">{label}</p> : null}
      {title ? <h2 className="mt-1.5 font-display text-[19px] font-semibold text-ink-900">{title}</h2> : null}
      {hint ? <p className="mt-1.5 text-[14px] leading-relaxed text-ink-600">{hint}</p> : null}
    </div>
    {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
  </div>
);

export const Toolbar = ({ children, testId }) => (
  <div data-testid={testId} className="flex flex-wrap items-center gap-2.5 rounded-2xl border border-border bg-white p-3">
    {children}
  </div>
);

export const SearchField = ({ value, onChange, placeholder = "Buscar", testId, className = "" }) => (
  <label className={`relative flex items-center ${className}`}>
    <Search size={16} className="absolute left-3.5 text-ink-400" aria-hidden="true" />
    <input
      data-testid={testId}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="h-11 w-full rounded-xl border border-border bg-white pl-10 pr-3.5 text-[14.5px] text-ink-800 placeholder:text-ink-400 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15"
    />
  </label>
);

export const SelectField = ({ value, onChange, children, testId, className = "" }) => (
  <select
    data-testid={testId}
    value={value}
    onChange={onChange}
    className={`h-11 rounded-xl border border-border bg-white px-3.5 text-[14.5px] text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15 ${className}`}
  >
    {children}
  </select>
);

export const Field = ({ label, hint, error, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-[13px] font-semibold uppercase tracking-[0.08em] text-ink-600">
      {label}
    </label>
    <div className="mt-2">{children}</div>
    {error ? (
      <p className="mt-1.5 text-[13px] text-danger">{error}</p>
    ) : hint ? (
      <p className="mt-1.5 text-[13px] leading-relaxed text-ink-500">{hint}</p>
    ) : null}
  </div>
);

const BUTTON_VARIANTS = {
  primary: "bg-sage-600 text-white hover:bg-sage-700",
  secondary: "border border-border bg-white text-ink-700 hover:bg-sand",
  ghost: "text-ink-600 hover:bg-sand",
  danger: "border border-danger/40 bg-white text-danger hover:bg-danger/10",
};

export const Action = ({ icon: Icon, children, variant = "primary", size = "md", className = "", ...rest }) => (
  <button
    type="button"
    className={`inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
      size === "sm" ? "min-h-[38px] px-4 text-[13.5px]" : "min-h-[44px] px-5 text-[14.5px]"
    } ${BUTTON_VARIANTS[variant]} ${className}`}
    {...rest}
  >
    {Icon ? <Icon size={size === "sm" ? 15 : 16} aria-hidden="true" /> : null}
    {children}
  </button>
);

const STATUS_TONES = {
  neutral: "bg-sand text-ink-700",
  active: "bg-success/12 text-success",
  pending: "bg-warning/18 text-[#7A5612]",
  progress: "bg-info/12 text-info",
  done: "bg-success/12 text-success",
  alert: "bg-danger/12 text-danger",
  brand: "bg-sage-600 text-white",
};

export const StatusBadge = ({ tone = "neutral", children, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-[12.5px] font-semibold ${STATUS_TONES[tone] || STATUS_TONES.neutral}`}
  >
    {children}
  </span>
);

export const Tag = ({ children }) => (
  <span className="inline-flex items-center rounded-full bg-sand px-2.5 py-1 text-[12.5px] font-medium text-ink-700">
    {children}
  </span>
);

export const DataTable = ({ columns, children, testId }) => (
  <div className="overflow-x-auto rounded-2xl border border-border bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
    <table data-testid={testId} className="w-full text-left">
      <thead>
        <tr className="border-b border-border bg-sand/45">
          {columns.map((c) => (
            <th
              key={c.key || c.label}
              className={`px-5 py-3.5 text-[12px] font-semibold uppercase tracking-[0.1em] text-ink-500 ${c.className || ""}`}
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-border/70">{children}</tbody>
    </table>
  </div>
);

export const Row = ({ children, testId, ...rest }) => (
  <tr data-testid={testId} className="transition-colors hover:bg-sand/35" {...rest}>
    {children}
  </tr>
);

export const Cell = ({ children, className = "" }) => (
  <td className={`px-5 py-4 align-top text-[14.5px] text-ink-700 ${className}`}>{children}</td>
);

export const EmptyState = ({ icon: Icon, title, description, action, testId }) => (
  <div
    data-testid={testId}
    className="flex flex-col items-start gap-3 rounded-2xl border border-dashed border-border bg-white/70 p-8"
  >
    {Icon ? (
      <span className="grid h-11 w-11 place-items-center rounded-full bg-sage-50 text-sage-700">
        <Icon size={20} aria-hidden="true" />
      </span>
    ) : null}
    <h3 className="font-display text-[18px] font-semibold text-ink-900">{title}</h3>
    {description ? <p className="max-w-lg text-[14.5px] leading-relaxed text-ink-600">{description}</p> : null}
    {action}
  </div>
);

export const MetricCard = ({ label, value, hint, icon: Icon, testId, alert = false }) => (  <Panel
    testId={testId}
    className={`flex items-start justify-between gap-4 ${
      alert ? "border-danger/45 bg-danger/[0.06] ring-1 ring-danger/20" : ""
    }`}
  >
    <div>
      <p className="er-label text-[12px] tracking-[0.14em]">{label}</p>
      <div className="mt-2.5 flex items-center gap-2.5">
        <p className={`font-display text-[32px] font-semibold leading-none ${alert ? "text-danger" : "text-ink-900"}`}>
          {value}
        </p>
        {alert ? (
          <span
            data-testid={testId ? `${testId}-badge` : undefined}
            className="rounded-full bg-danger px-2.5 py-1 text-[12px] font-bold text-white"
          >
            {value} pendente{value === 1 ? "" : "s"}
          </span>
        ) : null}
      </div>
      {hint ? <p className="mt-2 text-[13.5px] leading-relaxed text-ink-500">{hint}</p> : null}
    </div>
    {Icon ? (
      <span
        className={`grid h-11 w-11 shrink-0 place-items-center rounded-full ${
          alert ? "bg-danger/12 text-danger" : "bg-sage-50 text-sage-700"
        }`}
      >
        <Icon size={19} aria-hidden="true" />
      </span>
    ) : null}
  </Panel>
);
